// frontend-website/src/telemetry/telemetryTracker.js
// Telemetry tracking library for marketing website analytics

import { SessionManager } from './sessionManager.js';
import { DeviceDetector } from './deviceDetector.js';
import { EventQueue } from './eventQueue.js';

/**
 * TelemetryTracker - Main tracking library for website analytics
 *
 * Features:
 * - Session management with automatic timeout
 * - Device/browser detection
 * - Event batching to reduce API calls
 * - UTM parameter extraction
 * - Privacy controls (respects cookie consent)
 */
class TelemetryTracker {
    constructor(config = {}) {
        this.config = {
            apiEndpoint: config.apiEndpoint || '/api/telemetry/track',
            sessionTimeout: config.sessionTimeout || 30 * 60 * 1000, // 30 minutes
            batchSize: config.batchSize || 10,
            flushInterval: config.flushInterval || 5000, // 5 seconds
            enableConsoleLogging: config.enableConsoleLogging || false,
            respectCookieConsent: config.respectCookieConsent !== false, // Default true
            ...config
        };

        this.sessionManager = new SessionManager(this.config.sessionTimeout);
        this.deviceDetector = new DeviceDetector();
        this.eventQueue = new EventQueue(
            this.config.batchSize,
            this.config.flushInterval,
            this._sendBatch.bind(this)
        );

        this.isInitialized = false;
        this.userId = null;
        this.utmParams = this._extractUtmParams();
    }

    /**
     * Initialize the telemetry tracker
     */
    init() {
        if (this.isInitialized) {
            this._log('Telemetry tracker already initialized');
            return;
        }

        // Check cookie consent
        if (this.config.respectCookieConsent && !this._hasAnalyticsConsent()) {
            this._log('Analytics tracking disabled - no cookie consent');
            return;
        }

        // Start session
        this.sessionManager.startSession();

        // Track session start event
        this._trackEvent('SESSION_START', {
            isNewSession: this.sessionManager.isNewSession()
        });

        // Setup page unload tracking
        this._setupUnloadTracking();

        this.isInitialized = true;
        this._log('Telemetry tracker initialized');
    }

    /**
     * Track a page view
     */
    trackPageView() {
        if (!this._canTrack()) return;

        const event = {
            sessionId: this.sessionManager.getSessionId(),
            userId: this.userId,
            eventType: 'PAGE_VIEW',
            pagePath: window.location.pathname,
            pageTitle: document.title,
            referrer: document.referrer || null,
            utmSource: this.utmParams.utm_source,
            utmMedium: this.utmParams.utm_medium,
            utmCampaign: this.utmParams.utm_campaign,
            ...this.deviceDetector.getDeviceInfo(),
            timestamp: new Date().toISOString()
        };

        this.eventQueue.add(event);
        this._log('Page view tracked', event);
    }

    /**
     * Track a custom event
     */
    trackEvent(eventType, eventData = {}) {
        if (!this._canTrack()) return;

        this._trackEvent(eventType, eventData);
    }

    /**
     * Track a click event
     */
    trackClick(elementId, elementText, eventData = {}) {
        if (!this._canTrack()) return;

        this._trackEvent('CLICK', {
            elementId,
            elementText,
            ...eventData
        });
    }

    /**
     * Track a form submission
     */
    trackFormSubmit(formId, formData = {}) {
        if (!this._canTrack()) return;

        this._trackEvent('FORM_SUBMIT', {
            formId,
            ...formData
        });
    }

    /**
     * Set user ID (for logged-in users)
     */
    setUserId(userId) {
        this.userId = userId;
        this._log('User ID set', userId);
    }

    /**
     * Flush all pending events immediately
     */
    flush() {
        return this.eventQueue.flush();
    }

    /**
     * Internal method to track events
     * @private
     */
    _trackEvent(eventType, eventData = {}) {
        const event = {
            sessionId: this.sessionManager.getSessionId(),
            userId: this.userId,
            eventType,
            eventData: Object.keys(eventData).length > 0 ? eventData : null,
            pagePath: window.location.pathname,
            pageTitle: document.title,
            referrer: document.referrer || null,
            utmSource: this.utmParams.utm_source,
            utmMedium: this.utmParams.utm_medium,
            utmCampaign: this.utmParams.utm_campaign,
            ...this.deviceDetector.getDeviceInfo(),
            timestamp: new Date().toISOString()
        };

        this.eventQueue.add(event);
        this._log(`Event tracked: ${eventType}`, event);
    }

    /**
     * Send a single event to the backend
     * @private
     */
    async _sendBatch(event) {
        try {
            const response = await fetch(this.config.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(event)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            this._log(`Event sent successfully: ${event.eventType}`);
            return true;
        } catch (error) {
            console.error('Failed to send telemetry event:', error);
            return false;
        }
    }

    /**
     * Extract UTM parameters from URL
     * @private
     */
    _extractUtmParams() {
        const params = new URLSearchParams(window.location.search);
        const utmParams = {
            utm_source: params.get('utm_source') || null,
            utm_medium: params.get('utm_medium') || null,
            utm_campaign: params.get('utm_campaign') || null
        };

        // Store UTM params in session storage for persistence across pages
        if (utmParams.utm_source || utmParams.utm_medium || utmParams.utm_campaign) {
            sessionStorage.setItem('telemetry_utm', JSON.stringify(utmParams));
        } else {
            // Try to load from session storage
            const stored = sessionStorage.getItem('telemetry_utm');
            if (stored) {
                return JSON.parse(stored);
            }
        }

        return utmParams;
    }

    /**
     * Check if user has given analytics consent
     * @private
     */
    _hasAnalyticsConsent() {
        // Check for cookie consent (matches cookie banner name: cookie_consent)
        const consent = this._getCookie('cookie_consent');

        // If consent cookie exists and is 'accepted', allow tracking
        if (consent === 'accepted') {
            return true;
        }

        // If no consent system is in place, allow tracking (for development)
        // In production, you might want to default to false
        return !consent; // No consent cookie means user hasn't been asked yet
    }

    /**
     * Get cookie value
     * @private
     */
    _getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }
        return null;
    }

    /**
     * Check if tracking is allowed
     * @private
     */
    _canTrack() {
        if (!this.isInitialized) {
            this._log('Tracker not initialized');
            return false;
        }

        if (this.config.respectCookieConsent && !this._hasAnalyticsConsent()) {
            this._log('Tracking blocked - no analytics consent');
            return false;
        }

        return true;
    }

    /**
     * Setup tracking for page unload (session end)
     * @private
     */
    _setupUnloadTracking() {
        // Use visibilitychange for more reliable tracking
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                // Track session end with duration
                const sessionDuration = this.sessionManager.getSessionDuration();
                this._trackEvent('SESSION_END', {
                    duration: sessionDuration
                });

                // Flush events immediately (use sendBeacon for reliability)
                this.eventQueue.flushSync();
            }
        });

        // Fallback to beforeunload
        window.addEventListener('beforeunload', () => {
            const sessionDuration = this.sessionManager.getSessionDuration();
            this._trackEvent('SESSION_END', {
                duration: sessionDuration
            });
            this.eventQueue.flushSync();
        });
    }

    /**
     * Console logging (only if enabled)
     * @private
     */
    _log(message, data = null) {
        if (this.config.enableConsoleLogging) {
            if (data) {
                console.log(`[Telemetry] ${message}`, data);
            } else {
                console.log(`[Telemetry] ${message}`);
            }
        }
    }
}

// Create singleton instance
let trackerInstance = null;

/**
 * Initialize telemetry tracker
 */
export function initTelemetry(config) {
    if (!trackerInstance) {
        trackerInstance = new TelemetryTracker(config);
        trackerInstance.init();
    }
    return trackerInstance;
}

/**
 * Get tracker instance
 */
export function getTracker() {
    if (!trackerInstance) {
        console.warn('[Telemetry] Tracker not initialized. Call initTelemetry() first.');
    }
    return trackerInstance;
}

/**
 * Track page view
 */
export function trackPageView() {
    const tracker = getTracker();
    if (tracker) {
        tracker.trackPageView();
    }
}

/**
 * Track custom event
 */
export function trackEvent(eventType, eventData) {
    const tracker = getTracker();
    if (tracker) {
        tracker.trackEvent(eventType, eventData);
    }
}

/**
 * Track click event
 */
export function trackClick(elementId, elementText, eventData) {
    const tracker = getTracker();
    if (tracker) {
        tracker.trackClick(elementId, elementText, eventData);
    }
}

/**
 * Track form submission
 */
export function trackFormSubmit(formId, formData) {
    const tracker = getTracker();
    if (tracker) {
        tracker.trackFormSubmit(formId, formData);
    }
}

/**
 * Set user ID
 */
export function setUserId(userId) {
    const tracker = getTracker();
    if (tracker) {
        tracker.setUserId(userId);
    }
}

/**
 * Flush pending events
 */
export function flushEvents() {
    const tracker = getTracker();
    if (tracker) {
        return tracker.flush();
    }
    return Promise.resolve();
}

export default TelemetryTracker;
